import { useCallback, useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { Check, X, HelpCircle, Users, Save, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const RSVP_OPTS = [
  { v: "ja", label: "Zusage", icon: Check, cls: "bg-[#48634A] text-white border-[#48634A]" },
  { v: "nein", label: "Absage", icon: X, cls: "bg-[#B74134] text-white border-[#B74134]" },
  { v: "offen", label: "Offen", icon: HelpCircle, cls: "bg-[#F4EFE6] text-[#4A4F46] border-[#E5DFD3]" },
];

const OVERNIGHT_OPTS = ["", "Zelt", "Camper", "Wohnmobil", "Ich suche Unterkunft", "Ich habe eine Unterkunft"];
const ARRIVAL_OPTS = ["", "Donnerstag", "Freitag", "Samstag", "Vorher, wir machen in der Nähe Urlaub"];

function computeStats(members) {
  return {
    total: members.length,
    ja: members.filter((m) => m.rsvp === "ja").length,
    nein: members.filter((m) => m.rsvp === "nein").length,
    offen: members.filter((m) => !["ja", "nein"].includes(m.rsvp)).length,
    overnight: members.filter((m) => m.rsvp === "ja" && (m.overnight_type || m.overnight)).length,
  };
}

export default function Members() {
  const { isAdmin } = useAuth();
  const [members, setMembers] = useState([]);

  const load = useCallback(() => api.get("/members").then((r) => setMembers(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => computeStats(members), [members]);
  const handleSaved = useCallback((updated) => {
    setMembers((ms) => ms.map((m) => (m.id === updated.id ? updated : m)));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="members-page">
      <div className="text-center mb-12">
        <p className="label-eyebrow mb-3">Mitglieder & Anmeldung</p>
        <h1 className="font-serif text-5xl lg:text-6xl tracking-tight">Wer kommt mit?</h1>
        <p className="text-[#4A4F46] mt-4 max-w-2xl mx-auto leading-relaxed">
          Bitte trage deine Angaben ein und klicke pro Zeile auf <strong>Speichern</strong>. So gehen keine Eingaben verloren.
        </p>
      </div>

      {members.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="members-stats">
          <StatTile label="Zusagen" value={stats.ja} accent="#3F7A42" tint="#DCEADD" />
          <StatTile label="Absagen" value={stats.nein} accent="#B74134" tint="#FADDD6" />
          <StatTile label="Offen" value={stats.offen} accent="#C97B5D" tint="#FDE8DC" />
          <StatTile label="Übernachten" value={stats.overnight} accent="#3F6E96" tint="#E0ECF6" />
        </div>
      )}

      {isAdmin && members.length > 0 && (
        <div className="flex justify-end mb-4">
          <a
            href={`${API}/members/export.csv?auth=${localStorage.getItem("ebel_token")}`}
            className="inline-flex items-center gap-2 bg-white border border-[#E5DFD3] hover:border-[#48634A] hover:text-[#48634A] text-[#1A1C19] rounded-full px-5 py-2 text-sm transition-colors"
            data-testid="members-export-csv"
          >
            <Download className="w-4 h-4" /> CSV-Export (für Excel / Google&nbsp;Sheets)
          </a>
        </div>
      )}

      {members.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E5DFD3] rounded-2xl">
          <Users className="w-10 h-10 text-[#48634A] mx-auto mb-3" />
          <p className="font-serif text-2xl">Noch keine Mitglieder hinterlegt</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[#E5DFD3] bg-white shadow-[0_8px_40px_-30px_rgba(72,99,74,0.4)]">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#E5DFD3] text-[#48634A]">
                <th className="label-eyebrow p-4 min-w-[180px]">Name</th>
                <th className="label-eyebrow p-4 min-w-[260px]">Teilnahme</th>
                <th className="label-eyebrow p-4 min-w-[220px]">Anreise</th>
                <th className="label-eyebrow p-4 min-w-[220px]">Übernachtung</th>
                <th className="label-eyebrow p-4 min-w-[180px]">Ernährung</th>
                <th className="label-eyebrow p-4 min-w-[220px]">Ich könnte so unterstützen</th>
                <th className="label-eyebrow p-4 min-w-[200px]">Anmerkungen</th>
                <th className="label-eyebrow p-4 text-right min-w-[140px]">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <MemberRow key={m.id} member={m} onSaved={handleSaved} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MemberRow({ member, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const cur = draft ?? member;
  const dirty = draft !== null;

  const setField = (field, value) => {
    setDraft((d) => ({ ...(d ?? member), [field]: value }));
  };

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const res = await api.put(`/members/${member.id}`, draft);
      onSaved(res.data);
      setDraft(null);
      toast.success("Gespeichert");
    } catch {
      toast.error("Konnte nicht gespeichert werden");
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-b border-[#E5DFD3] last:border-b-0 align-top hover:bg-[#FDFBF7]/60" data-testid={`member-row-${member.id}`}>
      <td className="p-4 font-serif text-xl text-[#1A1C19]">{member.name}</td>
      <td className="p-4">
        <div className="inline-flex rounded-full border border-[#E5DFD3] overflow-hidden">
          {RSVP_OPTS.map((o) => {
            const Icon = o.icon;
            const active = cur.rsvp === o.v;
            return (
              <button
                key={o.v}
                type="button"
                onClick={() => setField("rsvp", o.v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${active ? o.cls : "bg-white text-[#4A4F46] hover:bg-[#F4EFE6]"}`}
                data-testid={`rsvp-${member.id}-${o.v}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {o.label}
              </button>
            );
          })}
        </div>
      </td>
      <td className="p-4">
        <select
          value={cur.arrival || ""}
          onChange={(e) => setField("arrival", e.target.value)}
          className="w-full bg-[#FDFBF7] border border-[#E5DFD3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#48634A]/30"
          data-testid={`arrival-${member.id}`}
        >
          <option value="">— bitte wählen —</option>
          {ARRIVAL_OPTS.filter(Boolean).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </td>
      <td className="p-4">
        <select
          value={cur.overnight_type || ""}
          onChange={(e) => setField("overnight_type", e.target.value)}
          className="w-full bg-[#FDFBF7] border border-[#E5DFD3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#48634A]/30"
          data-testid={`overnight-${member.id}`}
        >
          <option value="">Nein, keine Übernachtung</option>
          {OVERNIGHT_OPTS.filter(Boolean).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </td>
      <td className="p-4">
        <input
          type="text"
          value={cur.diet || ""}
          onChange={(e) => setField("diet", e.target.value)}
          placeholder="z. B. vegetarisch"
          className="w-full bg-[#FDFBF7] border border-[#E5DFD3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#48634A]/30"
          data-testid={`diet-${member.id}`}
        />
      </td>
      <td className="p-4">
        <input
          type="text"
          value={cur.bringing || ""}
          onChange={(e) => setField("bringing", e.target.value)}
          placeholder="z. B. Salat, Auf-/Abbau"
          className="w-full bg-[#FDFBF7] border border-[#E5DFD3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#48634A]/30"
          data-testid={`bringing-${member.id}`}
        />
      </td>
      <td className="p-4">
        <input
          type="text"
          value={cur.notes || ""}
          onChange={(e) => setField("notes", e.target.value)}
          placeholder="z. B. Anreise spät"
          className="w-full bg-[#FDFBF7] border border-[#E5DFD3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#48634A]/30"
          data-testid={`notes-${member.id}`}
        />
      </td>
      <td className="p-4 text-right">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary rounded-xl px-4 py-2 text-sm disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          data-testid={`save-${member.id}`}
        >
          <Save className="w-4 h-4" />
          {saving ? "…" : "Speichern"}
        </button>
      </td>
    </tr>
  );
}

function StatTile({ label, value, accent, tint = "#FFFFFF" }) {
  return (
    <div
      className="rounded-2xl overflow-hidden shadow-[0_8px_28px_-18px_rgba(72,99,74,0.4)] transition-transform hover:-translate-y-1 border"
      style={{ borderColor: `${accent}55` }}
    >
      <div className="px-4 py-2 text-white text-center" style={{ backgroundColor: accent }}>
        <p className="font-semibold uppercase tracking-[0.18em]" style={{ fontSize: "0.7rem" }}>{label}</p>
      </div>
      <div className="p-5 text-center" style={{ backgroundColor: tint }}>
        <p className="font-serif text-4xl tracking-tight" style={{ color: accent }}>{value}</p>
      </div>
    </div>
  );
}
